import { useForm, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { FormElement } from '../FormElement';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputText } from '../Inputs/InputText';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../core/Button';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { toast } from '@/hooks/useToast';
import {
  AgeRange,
  BirthYearRange,
  CompleteOffering,
  DeliveryMethod,
} from '@/types/offering';
import { Section } from '../Section';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import {
  useAuthTokenReseller,
  useAuthResellerTokenFromUrl,
} from '@/hooks/useAuthTokenReseller';
import { DateTime } from 'luxon';
import {
  useBearersFind,
  useTravellersFind,
  useOfferingGrant,
  useOfferingNotify,
  useOfferingValidateRecipient,
  OfferingGrantDTO,
} from '@/api/reseller';
import { slugify } from '@/helpers/slugify';
import { OfferingGranted } from './OfferingGranted';
import { isValidEmail } from '@/helpers/validation';
import { useConfig } from '@/contexts/ConfigContext';
import { useTheme } from '@/stores/theme';
import { OfferingDeliverySection } from './OfferingDeliverySection';
import { Spinner } from '../core/Spinner';
import { IconCheckCircle } from '@/icons';

export interface OfferingFormValues {
  recipientNin: string;
  deliveryMethod: DeliveryMethod;
  email: string;
  confirmationEmail: string;
  travellerId: string;
  cardNumber?: string;
  cardBearerId?: string;
  cardTravellerId: string;
  termsAccepted: boolean;
}

interface TicketOfferFormProps {
  offering: CompleteOffering;
}

export const TicketOfferForm = ({ offering }: TicketOfferFormProps) => {
  const { resellerUrl } = useConfig();
  const { formatMessage } = useIntl();
  const [formRef] = useAutoAnimateConditional<HTMLFormElement>();
  const customErrorMap = useCustomErrorMap;
  useAuthResellerTokenFromUrl();
  const grantOffer = useOfferingGrant();
  const notifyOffer = useOfferingNotify();
  const validateRecipient = useOfferingValidateRecipient();
  const { isTokenValid } = useAuthTokenReseller();

  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('app');
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [cardSearch, setCardSearch] = useState<string>('');
  const [orderSuccess, setOrderSuccess] = useState<{
    createdAt: string;
    orderId?: string;
    ticketId?: string;
  } | null>(null);
  const [sparToken, setSparToken] = useState<string | null>(null);
  const [sparError, setSparError] = useState<string | null>(null);
  const [validatedNin, setValidatedNin] = useState<string | null>(null);

  const requiresSparValidation = !!offering.spar_region_validation;

  const ticketOfferSchema = useMemo(() => {
    return z.object({
      recipientNin: z.string().regex(/^\d{8}-?\d{4}$/, {
        message: formatMessage({
          id: 'ticketOffer.error.ninFormat',
          defaultMessage:
            'Personnummer måste anges som YYYYMMDDXXXX eller YYYYMMDD-XXXX',
        }),
      }),
      deliveryMethod: z.enum(['app', 'card', 'other']),
      email: z.string().refine(
        (val) => {
          if (deliveryMethod === 'app') {
            if (val && isValidEmail(val)) {
              setEmailSearch(val);
            }
            return !!val;
          }
          return true;
        },
        {
          message: formatMessage({
            id: 'ticketOffer.error.emailAppRequired',
            defaultMessage: 'En giltig e-post krävs för app-leverans',
          }),
        },
      ),
      travellerId: z.string().refine(
        (val) => {
          if (deliveryMethod === 'other') {
            return true;
          }
          return !!val;
        },
        {
          message: formatMessage({
            id: 'form.required',
            defaultMessage: 'Fältet är obligatoriskt',
          }),
        },
      ),
      cardNumber: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (deliveryMethod !== 'card') {
              return true;
            }
            const normalized = val?.replace(/\s+/g, '') ?? '';
            if (normalized.length !== 16) {
              setCardSearch('');
              return false;
            }
            setCardSearch(normalized);
            return true;
          },
          {
            message: formatMessage({
              id: 'ticketOffer.error.cardNumberLength',
              defaultMessage: 'Kortnummer måste vara 16 tecken',
            }),
          },
        ),
      cardBearerId: z
        .string()
        .optional()
        .refine((val) => {
          if (deliveryMethod === 'card') {
            return !!val;
          }
          return true;
        }),
      cardTravellerId: z.string().refine((val) => {
        if (deliveryMethod === 'card') {
          return !!val;
        }
        return true;
      }),
      termsAccepted: z.boolean(),
      confirmationEmail: z
        .string()
        .trim()
        .superRefine((val, ctx) => {
          const isRequired =
            deliveryMethod === 'other' ||
            offering.transactions_tracked_externally;
          if (isRequired && !val) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage({
                id: 'ticketOffer.error.emailRequiredForDelivery',
                defaultMessage: 'E-post krävs',
              }),
            });
            return;
          }
          if (val && !isValidEmail(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: formatMessage({
                id: 'ticketOffer.error.invalidEmail',
                defaultMessage: 'Ange en giltig e-postadress',
              }),
            });
          }
        }),
    });
  }, [deliveryMethod, formatMessage, offering.transactions_tracked_externally]);

  const { handleSubmit, control, formState, watch, setValue, getValues } =
    useForm<OfferingFormValues>({
      resolver: zodResolver(ticketOfferSchema, {
        errorMap: customErrorMap(),
      }),
      mode: 'onChange',
      defaultValues: {
        recipientNin: '',
        deliveryMethod: 'app',
        email: '',
        confirmationEmail: '',
        travellerId: '',
        cardNumber: '',
        cardBearerId: '',
        cardTravellerId: '',
        termsAccepted: false,
      },
    });

  const { data: travellers, isLoading: isLoadingTravellers } =
    useTravellersFind(
      {
        email: emailSearch,
      },
      {
        query: {
          enabled: !!emailSearch,
        },
      },
    );

  const { data: cardBearer, isLoading: isLoadingBearers } = useBearersFind(
    {
      card_serial: cardSearch,
    },
    {
      query: {
        enabled: !!cardSearch,
      },
    },
  );

  useEffect(() => {
    if (!travellers) return;

    if (travellers.length > 0) {
      setValue('travellerId', travellers[0].id, { shouldDirty: true });
    } else {
      setValue('travellerId', '', { shouldDirty: true });
    }
  }, [travellers, setValue]);

  useEffect(() => {
    if (cardBearer) {
      setValue('cardBearerId', cardBearer.id, { shouldDirty: true });
      setValue('cardTravellerId', cardBearer.traveller_id || '', {
        shouldDirty: true,
      });
      setValue('travellerId', cardBearer.traveller_id || '', {
        shouldDirty: true,
      });
    } else {
      setValue('cardBearerId', '', { shouldDirty: true });
      setValue('cardTravellerId', '', { shouldDirty: true });
      setValue('travellerId', '', { shouldDirty: true });
    }
  }, [cardBearer, setValue]);

  const getFieldError = useCallback(
    (fieldName: keyof OfferingFormValues) => {
      const error = formState.errors[fieldName];
      const touchedField = formState.touchedFields[fieldName];
      const value = watch(fieldName);

      // Only show error if field has been touched and has a value
      if (touchedField && value && value.toString().length > 0 && error) {
        return error;
      }
      return undefined;
    },
    [formState, watch],
  );

  const onChangeDeliveryMethod = useCallback(
    (value: DeliveryMethod) => {
      setDeliveryMethod(value);
      setValue('deliveryMethod', value, { shouldDirty: true });
    },
    [setValue],
  );

  const handleSubmitError = useCallback(
    (error: unknown) => {
      console.error(error);
      if (
        isAxiosError(error) &&
        error.response?.status === 403 &&
        error.response.data?.message ===
          'self-service user at maximum number of issued grants'
      ) {
        toast.error({
          title: formatMessage({
            defaultMessage: 'Du har nått gränsen för antal beställningar',
          }),
          description: formatMessage({
            defaultMessage:
              'Kontakta kundtjänst om du behöver hjälp med din beställning.',
          }),
        });
        return;
      }
      if (
        isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data?.message.includes('no suitable bearers')
      ) {
        toast.error({
          title: formatMessage({
            defaultMessage: 'Ingen bärare hittad',
          }),
          description: formatMessage({
            defaultMessage:
              'Det finns ingen app/telefon eller andra bärare på kontot.',
          }),
        });
        return;
      }
      toast.error({
        title: formatMessage({
          defaultMessage: 'Något gick fel',
        }),
        description: error instanceof Error ? error.message : undefined,
      });
    },
    [formatMessage],
  );

  const onSubmit: SubmitHandler<OfferingFormValues> = useCallback(
    (data) => {
      if (deliveryMethod === 'other') {
        if (notifyOffer.isPending) {
          return;
        }
        notifyOffer.mutate(
          {
            id: offering.id,
            data: { email: data.confirmationEmail.trim() },
          },
          {
            onSuccess: (result) => {
              setOrderSuccess({
                createdAt: new Date().toISOString(),
                orderId: result.purchase_id,
              });
            },
            onError: handleSubmitError,
          },
        );
        return;
      }

      if (grantOffer.isPending) {
        return;
      }

      if (requiresSparValidation && !sparToken) {
        console.error('No SPAR token found');
        toast.error({
          title: formatMessage({
            defaultMessage: 'Något gick fel. Ladda om sidan och försök igen.',
          }),
        });
        return;
      }

      const confirmation =
        deliveryMethod === 'app'
          ? data.confirmationEmail.trim() || data.email.trim()
          : data.confirmationEmail.trim();

      const sparFields =
        requiresSparValidation && sparToken
          ? {
              recipient_nin: data.recipientNin.replace('-', ''),
              spar_token: sparToken,
            }
          : {};

      const submitData: OfferingGrantDTO = {
        traveller_id: data.travellerId,
        offer_id: offering.offers[0].id,
        ...(confirmation ? { email: confirmation } : {}),
        ...sparFields,
      };

      if (deliveryMethod !== 'app') {
        submitData.bearer_id = data.cardBearerId;
      }

      grantOffer.mutate(
        {
          id: offering.id,
          data: submitData,
        },
        {
          onSuccess: (result) => {
            setOrderSuccess({
              createdAt: result.created_at,
              orderId: result.id,
              ticketId: result.ticket_id,
            });
          },
          onError: handleSubmitError,
        },
      );
    },
    [
      deliveryMethod,
      grantOffer,
      requiresSparValidation,
      sparToken,
      offering.offers,
      offering.id,
      handleSubmitError,
      notifyOffer,
      formatMessage,
    ],
  );

  const watchRecipientNin = watch('recipientNin');

  const normalizedNin = watchRecipientNin?.replace('-', '') ?? '';
  const isRecipientNinValid =
    !!watchRecipientNin && /^\d{8}-?\d{4}$/.test(watchRecipientNin);

  const recipientAge = isRecipientNinValid
    ? Math.floor(calculateAge(normalizedNin))
    : null;

  const recipientBirthYear = isRecipientNinValid
    ? parseInt(normalizedNin.substring(0, 4))
    : null;

  const { isRecipientAgeValid, recipientAgeError } = useMemo(() => {
    type AgeCheck = { valid: boolean; error: string };

    const evaluateAgeRange = (range: AgeRange): AgeCheck => {
      if (recipientAge === null || recipientAge === undefined) {
        return { valid: false, error: '' };
      }
      if (range.min === undefined && range.max === undefined) {
        return { valid: true, error: '' };
      }
      if (range.min === undefined) {
        return {
          valid: recipientAge <= range.max!,
          error: formatMessage(
            { defaultMessage: 'Mottagaren måste vara {maxAge} år eller yngre' },
            { maxAge: range.max },
          ),
        };
      }
      if (range.max === undefined) {
        return {
          valid: recipientAge >= range.min,
          error: formatMessage(
            { defaultMessage: 'Mottagaren måste vara {minAge} år eller äldre' },
            { minAge: range.min },
          ),
        };
      }
      return {
        valid: recipientAge >= range.min && recipientAge <= range.max,
        error: formatMessage(
          {
            defaultMessage:
              'Mottagaren måste vara mellan {minAge} och {maxAge} år',
          },
          { minAge: range.min, maxAge: range.max },
        ),
      };
    };

    const evaluateBirthYearRange = (range: BirthYearRange): AgeCheck => {
      if (recipientBirthYear === null) {
        return { valid: false, error: '' };
      }
      if (range.min === undefined && range.max === undefined) {
        return { valid: true, error: '' };
      }
      if (range.min === undefined) {
        return {
          valid: recipientBirthYear <= range.max!,
          error: formatMessage(
            { defaultMessage: 'Mottagaren måste vara född senast {maxYear}' },
            { maxYear: range.max },
          ),
        };
      }
      if (range.max === undefined) {
        return {
          valid: recipientBirthYear >= range.min,
          error: formatMessage(
            { defaultMessage: 'Mottagaren måste vara född minst {minYear}' },
            { minYear: range.min },
          ),
        };
      }
      return {
        valid:
          recipientBirthYear >= range.min && recipientBirthYear <= range.max,
        error: formatMessage(
          {
            defaultMessage:
              'Mottagaren måste vara född mellan {minYear} och {maxYear}',
          },
          { minYear: range.min, maxYear: range.max },
        ),
      };
    };

    const checks: AgeCheck[] = [];
    if (offering.ageRange) {
      checks.push(evaluateAgeRange(offering.ageRange));
    }
    if (offering.birthYearRange) {
      checks.push(evaluateBirthYearRange(offering.birthYearRange));
    }

    const isRecipientAgeValid = checks.every((check) => check.valid);
    let recipientAgeError = checks.find((check) => !check.valid)?.error ?? '';

    if (recipientAgeError && offering.customAgeError) {
      recipientAgeError = offering.customAgeError;
    }

    return { isRecipientAgeValid, recipientAgeError };
  }, [
    offering.ageRange,
    offering.birthYearRange,
    offering.customAgeError,
    recipientAge,
    formatMessage,
    recipientBirthYear,
  ]);

  const sparValidationSatisfied = !requiresSparValidation || !!sparToken;

  useEffect(() => {
    if (!requiresSparValidation || validateRecipient.isPending) {
      return;
    }

    if (!isRecipientAgeValid) {
      setSparToken(null);
      setSparError(null);
      setValidatedNin(null);
      return;
    }

    if (validatedNin === normalizedNin) {
      return;
    }

    setSparToken(null);
    setSparError(null);

    validateRecipient.mutate(
      {
        id: offering.id,
        data: { recipient_nin: normalizedNin },
      },
      {
        onSuccess: (result) => {
          setValidatedNin(normalizedNin);
          setSparToken(result.token ?? '');
        },
        onError: (err) => {
          console.error(err);
          setValidatedNin(normalizedNin);

          if (!isAxiosError(err)) {
            setSparError(
              formatMessage({
                defaultMessage: 'Kunde inte validera mottagaren',
              }),
            );
            return;
          }

          const message = err.response?.data.message ?? '';
          if (message.includes('no SPAR record found')) {
            setSparError(
              formatMessage({
                defaultMessage: 'Kunde inte hitta personnumret',
              }),
            );
          } else if (
            message.includes(
              'recipient NIN has already been used for this offering',
            )
          ) {
            setSparError(
              formatMessage({
                defaultMessage:
                  'Mottagarens personnummer har redan använts för detta erbjudande',
              }),
            );
          } else if (
            message.includes('recipient not registered in allowed municipality')
          ) {
            setSparError(
              formatMessage({
                defaultMessage:
                  'Mottagaren inte folkbokförd inom tillåten kommun',
              }),
            );
          } else {
            setSparError(
              formatMessage({
                defaultMessage: 'Något gick fel',
              }),
            );
          }
        },
      },
    );
  }, [
    requiresSparValidation,
    isRecipientAgeValid,
    normalizedNin,
    offering.id,
    validatedNin,
    validateRecipient,
    formatMessage,
  ]);

  if (orderSuccess) {
    return (
      <OfferingGranted
        method={deliveryMethod}
        offering={offering}
        createdAt={orderSuccess.createdAt}
        orderId={orderSuccess.orderId}
        ticketId={orderSuccess.ticketId}
      />
    );
  }

  if (!isTokenValid()) {
    return (
      <ContainerSection offering={offering} description={offering.welcome}>
        <Button
          variant="filled"
          className="w-full"
          onClick={() => {
            const callbackUrl = slugify(window.location.href);
            const url = `${resellerUrl}/selfservice/offerings/${offering.url}?callback_url=${callbackUrl}`;
            window.location.href = url;
          }}
        >
          <FormattedMessage
            id="ticketOffer.loginBankId"
            defaultMessage="Logga in med BankID"
          />
        </Button>
      </ContainerSection>
    );
  }

  return (
    <ContainerSection offering={offering} description={offering.description}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <FormElement
          input={
            <div className="flex items-center gap-2">
              <InputText
                label={
                  offering.recipientNinLabel ??
                  formatMessage({
                    id: 'ticketOffer.recipientNinLabel',
                    defaultMessage: 'Mottagarens personnummer (12 siffror)',
                  })
                }
                name="recipientNin"
                control={control}
                editMode={true}
                error={getFieldError('recipientNin')}
                placeholder="YYYYMMDD-XXXX"
                helperTextReserveSpace
              />
              {validateRecipient.isPending && (
                <Spinner aria-hidden="true" className="size-5" />
              )}
              {isRecipientNinValid && !validateRecipient.isPending && (
                <IconCheckCircle
                  aria-label={formatMessage({
                    defaultMessage: 'Personnummer godkänt',
                  })}
                  className="size-5 text-green-500"
                />
              )}
            </div>
          }
        />

        {!isRecipientAgeValid && recipientAgeError ? (
          <p className="text-red-500" role="alert">
            {recipientAgeError}
          </p>
        ) : null}

        {isRecipientAgeValid &&
          requiresSparValidation &&
          !validateRecipient.isPending &&
          sparError && (
            <p className="text-red-500" role="alert">
              {sparError}
            </p>
          )}

        {isRecipientAgeValid && sparValidationSatisfied && (
          <OfferingDeliverySection
            offering={offering}
            deliveryMethod={deliveryMethod}
            onChangeDeliveryMethod={onChangeDeliveryMethod}
            control={control}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            getFieldError={getFieldError}
            isLoadingTravellers={isLoadingTravellers}
            isLoadingBearers={isLoadingBearers}
            isSubmitting={grantOffer.isPending || notifyOffer.isPending}
          />
        )}
      </form>
    </ContainerSection>
  );
};

interface ContainerSectionProps {
  offering: CompleteOffering;
  description: ReactNode;
  children: ReactNode;
}

const ContainerSection = ({
  offering,
  description,
  children,
}: ContainerSectionProps) => {
  const { name, logo, logoDark } = offering;
  const { isDark } = useTheme();

  return (
    <Section className="bg-background-50">
      <div className="mb-4 flex flex-wrap items-center gap-5">
        <img
          src={isDark ? logoDark : logo}
          alt=""
          role="presentation"
          className="w-fit max-w-40 object-cover"
        />
        <h1 className="text-3xl font-bold">{name}</h1>
      </div>
      <div className="mb-10 flex flex-col gap-5 whitespace-pre-line [&_b]:font-bold [&_strong]:font-bold">
        {description}
      </div>
      {children}
    </Section>
  );
};

function calculateAge(nin: string): number {
  const year = parseInt(nin.substring(0, 4));
  const month = parseInt(nin.substring(4, 6));
  const day = parseInt(nin.substring(6, 8));

  const birthDate = DateTime.fromObject({ year, month, day });
  const now = DateTime.now();

  return now.diff(birthDate, 'years').years;
}
